// src/lib/autograb.ts
// Autograb API client for vehicle lookup, valuation, and PPSR checks.
//
// Contract verified against the live Autograb API + official docs
// (devhub.autograb.com), Aug 2026:
//   - Auth:      single header `ApiKey: <key>` — added server-side by
//                functions/api/autograb/[[path]].js. The key never reaches the browser.
//   - VIN:       GET  /vehicles/vins/{vin}?region=au
//   - Rego:      GET  /vehicles/registrations/{plate}?region=au&state={STATE}
//   - Valuation: POST /valuations/predict            {region, vehicle_id, odometer?}
//   - PPSR:      POST /certificates/generate         {rego, state, type:'ppsr'}
// Vehicle lookups return data nested under `vehicle`; colour is top-level.
// Market value comes from the separate /valuations/predict call (retail_price).

import axios from 'axios'
import { supabase } from './supabase'

// Every call goes through our own Pages Function, which checks the caller is
// signed in and adds the AutoGrab key from a server-side secret (29 Oct 2026).
// Before that the key was read from VITE_AUTOGRAB_API_KEY and hard-coded here,
// which shipped it to every browser.
const AUTOGRAB_BASE_URL = '/api/autograb'
const AUTOGRAB_REGION = import.meta.env.VITE_AUTOGRAB_REGION || 'au'
// Vehicle photos (stock + marketplace overlay) are a separate, contract-gated
// Autograb product. As of Aug 2026 this account's key returns 401 "No active
// contracts for the product" / 400 "disallowed overlay feature", so photo
// fetching is OFF by default to avoid guaranteed-failing calls on every lookup.
// Flip VITE_AUTOGRAB_PHOTOS_ENABLED to "true" once Autograb enables the images
// product on the account and it will start working with no code change.
const AUTOGRAB_PHOTOS_ENABLED =
  String(import.meta.env.VITE_AUTOGRAB_PHOTOS_ENABLED).toLowerCase() === 'true'

export type AustralianState = 'NSW' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'NT' | 'ACT'

export interface VehicleData {
  vin: string
  make: string
  model: string
  year: number
  odometer?: number
  color?: string
  bodyType?: string
  transmission?: string
  engine?: string
  fuelType?: string
  marketValue?: number
  tradeValue?: number
  registrationNumber?: string
  state?: AustralianState
  autograbVehicleId?: string
  photoUrl?: string
  additionalPhotos?: string[]
  photoSource?: 'marketplace' | 'stock'
}

export interface PhotoData {
  primary: string | null
  additional: string[]
  source: 'marketplace' | 'stock' | null
}

export interface PPSRData {
  vin: string
  status: 'clear' | 'encumbered' | 'stolen' | 'written_off' | 'error'
  encumbered: boolean
  stolen?: boolean
  writtenOff?: boolean
  certificateUrl?: string
  details?: Record<string, any>
}

export interface AutograbError {
  code: string
  message: string
  statusCode: number
}

// Single client, pointed at our relay. The relay needs the signed-in user's
// Supabase token; it adds the AutoGrab `ApiKey` header itself.
// validateStatus:()=>true so we inspect status ourselves rather than throw.
const autograbClient = axios.create({
  baseURL: AUTOGRAB_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 20000,
  validateStatus: () => true,
})

autograbClient.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  return config
})

/**
 * Get an estimated market (retail) value for a vehicle by its Autograb id.
 * Non-throwing: returns undefined market value on any failure so a lookup
 * still succeeds even if valuation is unavailable.
 */
async function getValuation(
  vehicleId: string,
  odometer?: number
): Promise<{ marketValue?: number; tradeValue?: number }> {
  try {
    const body: Record<string, any> = { region: AUTOGRAB_REGION, vehicle_id: vehicleId }
    if (odometer && odometer > 0) body.odometer = odometer

    const res = await autograbClient.post('/valuations/predict', body)
    const prediction = res.data?.prediction
    if (res.status >= 200 && res.status < 300 && prediction) {
      const retail = prediction.retail_price ?? prediction.price
      return {
        marketValue: retail != null ? Number(retail) : undefined,
        tradeValue: prediction.trade_price != null ? Number(prediction.trade_price) : undefined,
      }
    }
  } catch (err) {
    console.error('Autograb valuation error:', err)
  }
  return {}
}

// Map the nested Autograb `vehicle` object (+ top-level colour) to VehicleData.
function mapVehicle(data: any, fallbackVin: string): VehicleData {
  const v = data.vehicle || {}
  return {
    autograbVehicleId: v.id != null ? String(v.id) : undefined,
    vin: v.vin || fallbackVin || '',
    make: v.make || '',
    model: v.model || '',
    year: parseInt(v.year) || parseInt(v.model_year) || new Date().getFullYear(),
    color: data.colour || v.colour || undefined,
    bodyType: v.body_type || undefined,
    transmission: v.transmission || undefined,
    engine: v.engine || undefined,
    fuelType: v.fuel || v.fuel_type || undefined,
    registrationNumber: data.plate_module?.plate || undefined,
  }
}

/**
 * Look up a vehicle by VIN, then fetch its market valuation.
 * Returns null when the VIN isn't found (so the UI can offer manual entry).
 */
export async function lookupVehicleByVIN(vin: string): Promise<VehicleData | null> {
  const clean = (vin || '').trim().toUpperCase()
  if (!clean || clean.length < 10) {
    throw new Error('Invalid VIN format')
  }

  const res = await autograbClient.get(
    `/vehicles/vins/${encodeURIComponent(clean)}?region=${AUTOGRAB_REGION}`
  )

  if (res.status < 200 || res.status >= 300 || !res.data?.success || !res.data?.vehicle) {
    console.error('Autograb VIN lookup: no match', { vin: clean, status: res.status })
    return null
  }

  const vehicle = mapVehicle(res.data, clean)
  if (vehicle.autograbVehicleId) {
    const val = await getValuation(vehicle.autograbVehicleId, vehicle.odometer)
    vehicle.marketValue = val.marketValue
    vehicle.tradeValue = val.tradeValue
  }
  return vehicle
}

/**
 * Look up a vehicle by registration plate. `state` is required by Autograb.
 * Returns null when not found.
 */
export async function lookupVehicleByRego(
  registration: string,
  state: AustralianState
): Promise<VehicleData | null> {
  const clean = (registration || '').trim().toUpperCase()
  if (!clean || clean.length < 2) {
    throw new Error('Invalid registration format')
  }
  if (!state) {
    throw new Error('State is required for a registration lookup')
  }

  const res = await autograbClient.get(
    `/vehicles/registrations/${encodeURIComponent(clean)}?region=${AUTOGRAB_REGION}&state=${state}`
  )

  if (res.status < 200 || res.status >= 300 || !res.data?.success || !res.data?.vehicle) {
    console.error('Autograb Rego lookup: no match', { rego: clean, state, status: res.status })
    return null
  }

  const vehicle = mapVehicle(res.data, '')
  vehicle.registrationNumber = clean
  vehicle.state = state
  if (vehicle.autograbVehicleId) {
    const val = await getValuation(vehicle.autograbVehicleId, vehicle.odometer)
    vehicle.marketValue = val.marketValue
    vehicle.tradeValue = val.tradeValue
  }
  return vehicle
}

/**
 * Generate a PPSR certificate for a vehicle by registration + state.
 * NOTE: this generates a real (billable) PPSR certificate. It requires a
 * registration and state — a VIN alone is not sufficient for this endpoint.
 * Non-blocking: returns status 'error' instead of throwing so deal creation
 * can proceed.
 */
export async function checkPPSR(
  registration: string,
  state: AustralianState,
  vinForRecord = ''
): Promise<PPSRData> {
  try {
    if (!registration || !state) {
      return { vin: vinForRecord, status: 'error', encumbered: false }
    }

    const res = await autograbClient.post('/certificates/generate', {
      rego: registration.trim().toUpperCase(),
      state,
      type: 'ppsr',
    })

    const cert = res.data?.certificate
    if (res.status < 200 || res.status >= 300 || !cert) {
      console.error('PPSR check failed', { registration, state, status: res.status })
      return { vin: vinForRecord, status: 'error', encumbered: false }
    }

    const encumbered = !!cert.has_secured_parties
    const stolen = !!cert.has_stolen_records
    const writtenOff = !!cert.has_written_off_records

    let status: PPSRData['status'] = 'clear'
    if (stolen) status = 'stolen'
    else if (writtenOff) status = 'written_off'
    else if (encumbered) status = 'encumbered'

    return {
      vin: cert.vin || vinForRecord,
      status,
      encumbered,
      stolen,
      writtenOff,
      certificateUrl: cert.url,
      details: cert,
    }
  } catch (err) {
    console.error('PPSR check error:', err)
    return { vin: vinForRecord, status: 'error', encumbered: false }
  }
}

/**
 * Fetch vehicle photos by Autograb vehicle id. Tries the marketplace overlay
 * (real listing photos) first, then stock photos. Non-blocking: always resolves,
 * returning empty data on any failure (photos never block deal creation).
 *
 * NOTE: both photo products are contract-gated. Disabled via
 * AUTOGRAB_PHOTOS_ENABLED until the account's Autograb plan includes them.
 * Endpoints verified against the live API + docs:
 *   marketplace: GET /sourcing/market_overlay/{id}?region=au&features=all_images
 *   stock:       GET /vehicles/{id}/photos?region=au&color={color}
 */
export async function getVehiclePhotos(
  vehicleId: string,
  color?: string
): Promise<PhotoData> {
  const empty: PhotoData = { primary: null, additional: [], source: null }
  if (!AUTOGRAB_PHOTOS_ENABLED || !vehicleId) return empty

  // 1) Marketplace listing photos (actual car).
  try {
    const res = await autograbClient.get(
      `/sourcing/market_overlay/${encodeURIComponent(vehicleId)}?region=${AUTOGRAB_REGION}&features=all_images`
    )
    const all = res.data?.all_images
    const cover = res.data?.cover_image_url
    if (res.status >= 200 && res.status < 300 && Array.isArray(all) && all.length > 0) {
      const primary = cover || all[0]
      const additional = all.filter((u: string) => u !== primary)
      return { primary, additional, source: 'marketplace' }
    }
  } catch (err) {
    console.debug('Autograb marketplace photos unavailable', err)
  }

  // 2) Stock photos (generic, colour-matched).
  try {
    const res = await autograbClient.get(
      `/vehicles/${encodeURIComponent(vehicleId)}/photos?region=${AUTOGRAB_REGION}&color=${encodeURIComponent(
        color || 'white'
      )}`
    )
    const images = res.data?.images
    if (res.status >= 200 && res.status < 300 && Array.isArray(images) && images.length > 0) {
      const primary = images[0]?.url || null
      return { primary, additional: [], source: primary ? 'stock' : null }
    }
  } catch (err) {
    console.debug('Autograb stock photos unavailable', err)
  }

  return empty
}

export interface CatalogVehicle {
  id: string
  make: string
  model: string
  series?: string
  title?: string
  year: number
  bodyType?: string
  fuelType?: string
  transmission?: string
  marketValue?: number
  tradeValue?: number
}

/**
 * Free-text search of the Autograb vehicle catalog.
 * Endpoint (verified live, NOT contract-gated):
 *   GET /vehicles?search={query}&region=au  -> { success, vehicles[], total, confidence }
 * The term generally needs make + model + year (e.g. "Honda Civic 2020"); a bare
 * make ("Honda") returns 404 "No vehicles found".
 * Each catalog match is a spec variant with an `id`; we valuate the top `limit`
 * of them (retail/trade) via /valuations/predict so the UI can show a price.
 * Returns [] on 404 / no matches (non-throwing).
 */
export async function searchVehicleCatalog(query: string, limit = 20): Promise<CatalogVehicle[]> {
  const q = (query || '').trim()
  if (!q) return []

  const res = await autograbClient.get('/vehicles', {
    params: { search: q, region: AUTOGRAB_REGION },
  })

  if (res.status < 200 || res.status >= 300 || !Array.isArray(res.data?.vehicles)) {
    return [] // 404 = term too broad / no matches
  }

  const vehicles = res.data.vehicles.slice(0, limit)
  return Promise.all(
    vehicles.map(async (v: any): Promise<CatalogVehicle> => {
      const id = v.id != null ? String(v.id) : ''
      let marketValue: number | undefined
      let tradeValue: number | undefined
      if (id) {
        const val = await getValuation(id)
        marketValue = val.marketValue
        tradeValue = val.tradeValue
      }
      return {
        id,
        make: v.make || '',
        model: v.model || '',
        series: v.series || undefined,
        title: v.title || undefined,
        year: parseInt(v.year) || parseInt(v.model_year) || 0,
        bodyType: v.body_type || undefined,
        fuelType: v.fuel_type || undefined,
        transmission: v.transmission_type || undefined,
        marketValue,
        tradeValue,
      }
    })
  )
}

/**
 * Calculate gross margin between purchase price and estimated sale (market) price.
 */
export function calculateMargin(purchasePrice: number, estimatedSalePrice: number) {
  const grossMargin = estimatedSalePrice - purchasePrice
  const marginPercent = purchasePrice > 0 ? (grossMargin / purchasePrice) * 100 : 0

  return {
    grossMargin: Math.round(grossMargin * 100) / 100,
    marginPercent: Math.round(marginPercent * 100) / 100,
    isLoss: grossMargin < 0,
  }
}

/**
 * Validate a calculated margin against admin thresholds.
 */
export function validateMargin(
  margin: ReturnType<typeof calculateMargin>,
  thresholdPercent: number,
  thresholdDollars: number
): { valid: boolean; warning?: string } {
  if (margin.isLoss) {
    return {
      valid: false,
      warning: `Loss deal: -$${Math.abs(margin.grossMargin).toLocaleString()}`,
    }
  }

  if (margin.marginPercent < thresholdPercent || margin.grossMargin < thresholdDollars) {
    return {
      valid: false,
      warning: `Margin below threshold: ${margin.marginPercent}% / $${margin.grossMargin.toLocaleString()}`,
    }
  }

  return { valid: true }
}

export default {
  lookupVehicleByVIN,
  lookupVehicleByRego,
  checkPPSR,
  getVehiclePhotos,
  calculateMargin,
  validateMargin,
}
